import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  Link,
  Stack,
  Grid as MuiGrid,
  CircularProgress,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import SelectSearchVendor from './SelectSearchVendor';
import type { Vendor } from '@repo/api-contract';
import { openOrderModal, closeModal } from '../../state/orderModalSlice';
import { useDispatch } from '../../state';
import { openSearchVendorModal } from '../../state/vendorSearchModalSlice';
import type { FormikProps } from 'formik';
import type { OrderCreateInput } from '@repo/api-contract';
import { openNewVendorModal } from '../../state/vendorModalSlice';
import { DataGridTranslatedColumnHeader } from '../dataGrid/DataGridTranslatedColumnHeader';

/* --------------------------------- helpers -------------------------------- */
function vendorToDisplay(v: any): string {
  if (!v) return '';
  return (
    v?.details?.companyName ??
    v?.companyName ??
    v?.code ??
    (typeof v === 'string' ? v : '')
  );
}

function openBrowseVendorModalAndReturnVendor(
  dispatch: ReturnType<typeof useDispatch>,
): Promise<Vendor | undefined> {
  return new Promise((resolve) => {
    const onImport = (selected: Vendor[]) => resolve(selected?.[0]);
    const onCancel = () => resolve(undefined);
    dispatch(closeModal());
    dispatch(openSearchVendorModal({ onImport, onCancel, multiSelect: false }));
  });
}

/* -------------------------- edit cell (plain look) ------------------------- */
type EditCellProps = GridRenderEditCellParams & {
  formik?: FormikProps<OrderCreateInput> | FormikProps<any>;
  /** Custom way to write the picked vendor into Formik. */
  applyToFormik?: (args: {
    formik: FormikProps<any>;
    picked: Vendor;
    gridId: GridRowId;
    field: string; // grid column field
  }) => void;
  /** Default “items-array” updater config (used if applyToFormik not provided) */
  defaultArrayKey?: string; // e.g., 'items' (default)
  defaultRowIdKey?: string; // e.g., 'itemId' (default)
};

function VendorPickerEditCell(props: EditCellProps) {
  const {
    id,
    field,
    value,
    api,
    formik,
    applyToFormik,
    defaultArrayKey = 'items',
    defaultRowIdKey = 'itemId',
  } = props;

  const [open, setOpen] = React.useState(false);
  const [currentVendor, setCurrentVendor] = React.useState<any>(
    typeof value === 'object' && value ? value : null,
  );

  const anchorRef = React.useRef<HTMLDivElement | null>(null);

  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const dispatch = useDispatch();

  const display = vendorToDisplay(currentVendor ?? value);

  const closeDropdown = () => setOpen(false);

  // Inline select while grid is mounted
  const applySelectedInline = async (vendor: any) => {
    // Close edit first so Grid doesn't re-apply stale cell value
    await new Promise<void>((resolve) => {
      api.stopCellEditMode({ id, field });
      setTimeout(resolve, 0); // Ensure it resolves after the stopCellEditMode call
    });
    setCurrentVendor(vendor);

    if (formik) {
      if (applyToFormik) {
        applyToFormik({ formik, picked: vendor, gridId: id, field });
      } else {
        const arr = formik.values?.[defaultArrayKey] ?? [];
        formik.setFieldValue(
          defaultArrayKey,
          arr.map((it: any) =>
            it?.[defaultRowIdKey] === id ? { ...it, [field]: vendor } : it,
          ),
        );
      }
    }
    // Old. DO NOT DELTE!
    /* This is needed as example how you trigger processRowUpdate of data grid pro
        setCurrentVendor(vendor);
        await api.setEditCellValue({ id, field, value: vendor });
        setOpen(false);
        // @ts-ignore
        api.stopCellEditMode({ id, field });
        */
    closeDropdown();
  };

  // Browse flow (grid unmounts): write via Formik after remount
  const handleBrowse = async () => {
    closeDropdown();
    const picked = await openBrowseVendorModalAndReturnVendor(dispatch);
    // Reopen Order modal (remounts grid)
    dispatch(openOrderModal());
    if (!picked || !formik) return;

    if (applyToFormik) {
      applyToFormik({ formik, picked, gridId: id, field });
    } else {
      // Default: update items[] element where (item[defaultRowIdKey] === grid id)
      const arr = formik.values?.[defaultArrayKey] ?? [];
      formik.setFieldValue(
        defaultArrayKey,
        arr.map((it: any) =>
          it?.[defaultRowIdKey] === id ? { ...it, [field]: picked } : it,
        ),
      );
    }
  };

  const newVendorCallBack = async (
    vendor: Vendor | undefined,
  ): Promise<void> => {
    dispatch(openOrderModal());
    if (!vendor || !formik) return;

    if (applyToFormik) {
      applyToFormik({ formik, picked: vendor, gridId: id, field });
    } else {
      const arr = formik.values?.[defaultArrayKey] ?? [];
      formik.setFieldValue(
        defaultArrayKey,
        arr.map((it: any) =>
          it?.[defaultRowIdKey] === id ? { ...it, [field]: vendor } : it,
        ),
      );
    }
  };

  const handleCreate = () => {
    closeDropdown();
    dispatch(closeModal());
    dispatch(openNewVendorModal({ createCallback: newVendorCallBack }));
  };

  const handleKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === 'Escape') closeDropdown();
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Anchor area (cell content) */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={display}
      >
        <span>{display}</span>
      </Box>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          maxWidth: 360,
          width: '100%',
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper onKeyDown={handleKeyDown} sx={{ p: 2 }}>
              <ClickAwayListener onClickAway={closeDropdown}>
                <Box>
                  <Stack gap={2} width="100%">
                    <MuiGrid>
                      <Stack gap={1}>
                        <SelectSearchVendor
                          label={tGC('Vendor')}
                          value={currentVendor}
                          disabled={false}
                          error={false}
                          helperText=""
                          onChange={(vendor) =>
                            void applySelectedInline(vendor)
                          }
                        />
                        <Box
                          sx={{
                            justifyContent: 'space-between',
                            display: 'flex',
                          }}
                        >
                          <Link
                            underline="always"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleBrowse}
                          >
                            {'Browse Vendor'}
                          </Link>
                          <Link
                            underline="always"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleCreate}
                          >
                            {'Add Vendor'}
                          </Link>
                        </Box>
                      </Stack>
                    </MuiGrid>
                  </Stack>

                  <Box
                    sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={closeDropdown}
                    >
                      {tGB('Close')}
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

/* ------------------------------ public factory ----------------------------- */
/* This column is cotrolled by formik because it has its own modals that trigget edit on clicks of data grid table.
because of that acidental uncotnrooled triggers we will use formik to control the value of the cell */
export function createVendorColumn(opts?: {
  formik: FormikProps<any>;
  /** Custom writer to Formik when the value comes from the Browse/Create flow. */
  applyToFormik: (args: {
    formik: FormikProps<any>;
    picked: Vendor;
    gridId: GridRowId;
    field: string;
  }) => void;
  /** Defaults for array-based forms (used only if applyToFormik is not provided). */
  defaultArrayKey?: string; // default 'items'
  defaultRowIdKey?: string; // default 'itemId'
  field?: string;
  headerName?: string;
  /** When set, header uses `GridColumnHeaderTitle` + i18n; `headerName` becomes `labelKey + labelSuffix`. */
  translatedHeader?: {
    keyPrefix: string;
    labelKey: string;
    labelSuffix?: string;
  };
  flex?: number;
  editable?: boolean; // default true
}): GridColDef {
  const field = opts?.field ?? 'vendor';
  const th = opts?.translatedHeader;
  const headerName = th
    ? th.labelKey + (th.labelSuffix ?? '')
    : opts?.headerName ?? 'Vendor';
  const flex = opts?.flex ?? 1.2;
  const editable = opts?.editable ?? true;

  return {
    field,
    headerName,
    flex,
    editable,
    ...(th
      ? {
          renderHeader: (params) => (
            <DataGridTranslatedColumnHeader
              params={params}
              keyPrefix={th.keyPrefix}
              labelKey={th.labelKey}
              labelSuffix={th.labelSuffix}
            />
          ),
        }
      : {}),
    renderCell: (params: GridRenderCellParams<any>) => {
      // per-row spinner based on row.loading
      if (params.row?.loadingV) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              pl: 1,
            }}
          >
            <CircularProgress size={16} />
          </Box>
        );
      }
      return <span>{vendorToDisplay(params.value)}</span>;
    },
    renderEditCell: (params: GridRenderEditCellParams) => (
      <VendorPickerEditCell
        {...(params as EditCellProps)}
        formik={opts?.formik}
        applyToFormik={opts?.applyToFormik}
        defaultArrayKey={opts?.defaultArrayKey}
        defaultRowIdKey={opts?.defaultRowIdKey}
      />
    ),
  };
}
